import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { setCompliantLocalStorageItem } from './redux/actions';
import { CompliantLocalStorageKeys } from './redux/constants';
import { getCompliantLocalStorageItem } from './redux/selectors';

export function useLocalStorageValue<T>(
  key: CompliantLocalStorageKeys,
  stringToValue: (value: string) => T,
  valueToString: (value: T) => string
): [T, (value: T) => void] {
  const localStorageValue: string = useSelector(partialRight(getCompliantLocalStorageItem, key));
  const [bufferValue, setBufferValue] = useState(stringToValue(localStorageValue));
  const dispatch = useDispatch();

  const handleSetBufferValue = useCallback(
    (value: T) => {
      setBufferValue(value);
      dispatch(setCompliantLocalStorageItem({ key, value: valueToString(value) }));
    },
    [dispatch, key, valueToString]
  );

  return [bufferValue, handleSetBufferValue];
}

export function useBooleanLocalStorageValue(key: CompliantLocalStorageKeys) {
  return useLocalStorageValue<boolean>(
    key,
    v => v === 'true',
    v => `${v}`
  );
}

export function useDateLocalStorageValue(key: CompliantLocalStorageKeys) {
  return useLocalStorageValue<Date>(
    key,
    v => new Date(v),
    v => v.toISOString()
  );
}
